import React, { useCallback, useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { ContainerNode } from './PageContainerNode';
import { usePageContainerDragEnd } from './usePageContainerDragEnd';
import { ROOT_DROPPABLE_PREFIX, sortByRowThenOrder } from './pageContainersHelpers';

interface PageContainersListProps {
  canvasId: string;
}

export const PageContainersList = ({ canvasId }: PageContainersListProps) => {
  const { getCanvas } = useUIModelState();

  const canvas = getCanvas(canvasId);

  const topLevelContainers = useMemo(
    () => [...(canvas?.containers || [])].sort(sortByRowThenOrder),
    [canvas?.containers]
  );

  // Local expand/collapse state — top-level expanded by default
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    (canvas?.containers || []).forEach((c) => initial.add(c.id));
    return initial;
  });

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleDragEnd = usePageContainerDragEnd(canvasId, topLevelContainers);

  if (!canvas) return null;

  return (
    <div className="tw-space-y-2">
      <FieldTitle label="Containers" size="sm" />

      {topLevelContainers.length === 0 ? (
        <div className="tw-text-sm tw-text-ink-secondary tw-text-center tw-py-4 tw-border tw-border-dashed tw-border-edge tw-rounded">
          No containers on this page yet
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId={`${ROOT_DROPPABLE_PREFIX}${canvasId}`}
            type="container-L0"
          >
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`tw-space-y-1 tw-rounded ${
                  snapshot.isDraggingOver ? 'tw-bg-accent-tint' : ''
                }`}
              >
                {topLevelContainers.map((container, index) => (
                  <Draggable
                    key={container.id}
                    draggableId={container.id}
                    index={index}
                  >
                    {(dragProvided, dragSnapshot) => (
                      <ContainerNode
                        container={container}
                        level={0}
                        expandedIds={expandedIds}
                        toggleExpanded={toggleExpanded}
                        dragProvided={dragProvided}
                        dragSnapshot={dragSnapshot}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default PageContainersList;
